import { useMemo } from 'react'
import { ICategory } from '../../../interfaces/catalog'
import { isString, sortByKey } from '../../../utils'
import useId from '../../hooks/useId'
import {
  CategoryListCard,
  CategoryListContent,
  CategoryListHeading,
  CategoryListIcon,
  CategoryListImage,
  CategoryListObserver,
  CategoryListWrapper
} from './CategoryList.styles'
import { CategoryListProps } from './CategoryList.types'

const CategoryList = ({
  id = 'CategoryList',
  className = '',
  categories = [],
  sortKey,
  padding,
  horizontal,
  radius,
  styles,
  card,
  image,
  heading,
  icon,
  loading,
  clickActions,
  displayImages = true,
  reachBottomActions,
  'data-testid': testid = 'category',
  ...props
}: CategoryListProps) => {
  const { getTestId } = useId(testid)
  const categoryList = useMemo(
    () => (sortKey ? sortByKey(categories, sortKey) : categories),
    [categories, sortKey]
  )
  const iconProps = isString(icon) ? { name: icon } : icon
  const handleClick = (category: ICategory) => {
    if (clickActions) {
      clickActions({ category })
    }
  }

  return (
    <div>
      <CategoryListWrapper
        id={id}
        className={`CategoryList ${className}`}
        sx={styles}
        data-testid={getTestId()}
        {...props}>
        {categoryList?.map((category) => (
          <CategoryListCard
            key={category.slug}
            id={`${id}-${category.slug}`}
            className="CategoryListCard"
            onClick={() => handleClick(category)}
            padding={padding}
            horizontal={horizontal}
            radius={radius}
            data-testid={getTestId(category.slug)}
            {...card}>
            {displayImages && (
              <CategoryListImage
                id={`${id}-${category.slug}-image`}
                className="CategoryListImage"
                src={category.image || ''}
                data-testid={getTestId(`${category.slug}-image`)}
                {...image}
              />
            )}
            <CategoryListContent
              id={`${id}-${category.slug}-content`}
              className="CategoryListContent">
              <CategoryListHeading
                id={`${id}-${category.slug}-heading`}
                className="CategoryListHeading"
                data-testid={getTestId(`${category.slug}-heading`)}
                {...heading}>
                {category.name}
              </CategoryListHeading>
              <CategoryListIcon
                id={`${id}-${category.slug}-icon`}
                className="CategoryListIcon"
                name="MdChevronRight"
                data-testid={getTestId(`${category.slug}-icon`)}
                {...iconProps}
              />
            </CategoryListContent>
          </CategoryListCard>
        ))}
      </CategoryListWrapper>
      <CategoryListObserver
        loading={loading}
        data={categories}
        data-testid={getTestId('observer')}
        entryActions={reachBottomActions}
      />
    </div>
  )
}

export default CategoryList
